import { Observable, UnaryFunction, catchError, map, of, pipe, tap } from "rxjs";

import { Injectable } from "@angular/core";
import { WebsocketService } from "../databases/websocket";

export interface RowsAffected {
    rows_affected: number;
}

export interface PayloadWithLogging<T> {
    data: T;
    logData?: Record<string, any>;
}

export enum ErrorToSuppress {
    PERMISSION_DENIED_ERROR = "permission denied",
}

@Injectable({
    providedIn: "root",
})
export class DbCrudHelper {
    constructor(private websocketService: WebsocketService) {}

    static throwIfZeroUpdates(): UnaryFunction<Observable<RowsAffected>, Observable<RowsAffected>> {
        return pipe(
            tap(({ rows_affected }) => {
                if (rows_affected <= 0) {
                    const msg = "No rows updated.";
                    console.error(msg);
                    throw Error(msg);
                }
                if (rows_affected > 1) {
                    console.error("Updated multiple rows.");
                }
            })
        );
    }

    static returnNullIfNotFound<T = any>(): UnaryFunction<Observable<T>, Observable<T | null>> {
        return pipe(
            catchError(error => {
                if (error === "record not found") {
                    return of(null);
                }

                throw error;
            })
        );
    }

    /**
     * Send db request and return json repsonse
     *
     * @param method API method name
     * @param params API params
     * @param errorsToSuppress optional array of error messages to suppress
     * @returns { Observable } request result
     */
    makeRequest<T>(method: string, params = {}, errorsToSuppress: string[] = [], errorsToUpdate: any = {}): Observable<T> {
        return this.websocketService
            .makeRequest<string>(
                {
                    request_type: "db",
                    session_id: "",
                    db_data: { method, params },
                },
                undefined,
                errorsToSuppress,
                errorsToUpdate
            )
            .pipe(map(resp => this.parse<T>(resp)));
    }

    parse<T = any>(data: string): T {
        return JSON.parse(data) as T;
    }

    /**
     * Check PATCH / DELETE response and throw error if no rows were updated
     */
    throwIfZeroUpdates(): UnaryFunction<Observable<RowsAffected>, Observable<RowsAffected>> {
        return DbCrudHelper.throwIfZeroUpdates();
    }

    /**
     * Check GET response and return null if no results
     */
    returnNullIfNotFound<T = any>(): UnaryFunction<Observable<T>, Observable<T | null>> {
        return DbCrudHelper.returnNullIfNotFound();
    }
}
